import * as React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import * as Device from 'react-device-detect';

import { useSession } from '@hooks';
import { useGetEventDetailedQuery } from '@graphql';
import { previewEventVar } from '@context';
import EventSkeleton from './EventSkeleton';
import { Description, FAQ, FindUs, Tags, Participants } from '../common';
import { DialogEditWrapper, DialogEditContext } from '../common/layout';
import Header from './header';
import Preview from './preview';
import Tickets from './Tickets';

interface EventDialogProps {
  handleBack: (toRoot: boolean) => void;
}

const EventDialog = (props: EventDialogProps) => {
  const { account } = useSession();
  const [searchParams] = useSearchParams();
  const eventId = useReactiveVar(previewEventVar);
  const params = useParams();
  const { t } = useTranslation('event');

  const accurateEventId = searchParams.get('e') ?? params.id ?? params.eventId ?? eventId;

  const { data, loading } = useGetEventDetailedQuery({
    skip: !accurateEventId,
    fetchPolicy: 'cache-and-network',
    variables: { id: accurateEventId ?? '' },
  });

  if (loading) {
    return <EventSkeleton />;
  }

  if (!data?.event) {
    return (
      <Stack
        p={5}
        spacing={3}
        justifyContent="center"
        alignItems="center"
        bgcolor="background.default"
        sx={{
          minHeight: Device.isDesktop ? '90dvh' : undefined,
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="h6">
          {t('text.This event could not be found. It does not exist or has been deleted.')}
        </Typography>
        <Button variant="contained" color="primary" onClick={props.handleBack}>
          {t('back', { ns: 'cta' })}
        </Button>
      </Stack>
    );
  }

  const { event } = data;
  const isOwner = account && account.id === event.creator.id;

  return (
    <DialogEditWrapper entity="event" event={event} isOwner={!!isOwner}>
      <DialogContent handleBack={props.handleBack} />
    </DialogEditWrapper>
  );
};

interface DialogContentProps extends EventDialogProps {}

function DialogContent(props: DialogContentProps) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('DialogContent must be used inside DialogEditWrapper');
  }

  const { event, isOwner, isManaging } = context;
  const ticketsRef = React.useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const eventId = useReactiveVar(previewEventVar);
  const params = useParams();

  const accurateEventId = searchParams.get('e') ?? params.id ?? params.eventId ?? eventId;
  const padding = isMobile ? 1 : 3;

  const scrollToTickets = () => {
    if (ticketsRef.current) {
      ticketsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!event) {
    return null;
  }

  return (
    <Stack sx={{ position: 'relative' }}>
      {/* OVERLAY GLOBAL */}
      {isManaging && (
        <Stack
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        />
      )}

      <Stack sx={{ position: 'relative' }}>
        <Header
          id={event.id}
          status={event.status}
          covers={event.covers}
          isOwner={isOwner}
          handleBack={props.handleBack}
        />
      </Stack>
      <Stack p={padding}>
        <Preview event={event} scrollToTickets={scrollToTickets} />
      </Stack>
      <Stack spacing={5} p={padding}>
        <Description entity="event" entityId={accurateEventId ?? ''} description={event.description} />
        <Tickets tickets={event.tickets} ticketsRef={ticketsRef} />
        <Participants
          eventId={event.id}
          creatorId={event.creator.id}
          participants={event.participants.list}
        />
        <FindUs placeId={event.address?.placeId} />
        <FAQ entity="event" entityId={accurateEventId ?? ''} FAQs={event.FAQs} />
        <Tags tags={event.tags} />
      </Stack>
    </Stack>
  );
}

export default React.memo(EventDialog);
